import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_CONFIG } from '../config/api';
import type {
  AutofacturaRestaurantInfo,
  AutofacturaOrderSummary,
  AutofacturaGenerarRequest,
  AutofacturaResult,
} from '../types/autofactura';
import type { ClienteFiscal, CatalogoRegimenFiscal, CatalogoUsoCFDI } from '../types/facturacion';

// Separate API instance for public autofactura endpoints (no auth token)
export const autofacturaApi = createApi({
  reducerPath: 'autofacturaApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_CONFIG.baseUrl.replace(/\/api$/, '/api'),
  }),
  endpoints: (builder) => ({
    getRestaurantInfo: builder.query<AutofacturaRestaurantInfo, string>({
      query: (code) => `/autofactura/${code}/info`,
    }),

    getOrderSummary: builder.query<AutofacturaOrderSummary, { code: string; orderNumber: number; total: number; cuenta?: number }>({
      query: ({ code, orderNumber, total, cuenta }) =>
        `/autofactura/${code}/order/${orderNumber}?total=${total}${cuenta ? `&cuenta=${cuenta}` : ''}`,
    }),

    lookupClienteByRfc: builder.query<ClienteFiscal, { code: string; rfc: string }>({
      query: ({ code, rfc }) => `/autofactura/${code}/cliente/${rfc}`,
    }),

    generarAutofactura: builder.mutation<AutofacturaResult, { code: string; data: AutofacturaGenerarRequest }>({
      query: ({ code, data }) => ({
        url: `/autofactura/${code}/generar`,
        method: 'POST',
        body: data,
      }),
    }),

    sendFacturaEmail: builder.mutation<{ success: boolean; message: string }, { code: string; facturaId: number; email: string }>({
      query: ({ code, facturaId, email }) => ({
        url: `/autofactura/${code}/factura/${facturaId}/email`,
        method: 'POST',
        body: { email },
      }),
    }),

    getRegimenesFiscales: builder.query<CatalogoRegimenFiscal[], string>({
      query: (code) => `/autofactura/${code}/catalogos/regimenes-fiscales`,
    }),

    getUsosCFDI: builder.query<CatalogoUsoCFDI[], string>({
      query: (code) => `/autofactura/${code}/catalogos/usos-cfdi`,
    }),
  }),
});

export const {
  useGetRestaurantInfoQuery,
  useLazyGetOrderSummaryQuery,
  useLazyLookupClienteByRfcQuery,
  useGenerarAutofacturaMutation,
  useSendFacturaEmailMutation,
  useGetRegimenesFiscalesQuery,
  useGetUsosCFDIQuery,
} = autofacturaApi;

// Helper to build PDF/XML download URLs
export const getFacturaPdfUrl = (code: string, facturaId: number) =>
  `${API_CONFIG.baseUrl.replace(/\/api$/, '/api')}/autofactura/${code}/factura/${facturaId}/pdf`;

export const getFacturaXmlUrl = (code: string, facturaId: number) =>
  `${API_CONFIG.baseUrl.replace(/\/api$/, '/api')}/autofactura/${code}/factura/${facturaId}/xml`;
